import React from 'react';

/**
 * Wraps the collapsed count on a dashboard widget card.
 *
 * A failed count must never render as a number. These cards derive their value from
 * `countData?.count || 0`, so a query that times out would otherwise display a confident
 * "0" - which reads as "nothing to do" when the real answer might be thousands. Showing
 * an explicit failure state is the safer behaviour.
 *
 * @param {object} props
 * @param {boolean} props.isLoading
 * @param {boolean} props.isError
 * @param {boolean} props.hasData - false until the count arrives, so we spin rather than show a stale 0
 * @param {() => void} [props.onRetry]
 * @param {string} [props.mutedColor] - matches the host widget's theme
 * @param {React.ReactNode} props.children
 */
export default function WidgetCountState({ isLoading, isError, hasData, onRetry, mutedColor, children }) {
    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <i className="fas fa-exclamation-triangle fa-2x mb-2" style={{ color: '#f59e0b' }} />
                <div className="text-sm" style={{ color: mutedColor }}>Count unavailable</div>
                {onRetry && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link p-0 mt-1"
                        onClick={onRetry}
                    >
                        <i className="fas fa-redo mr-1" /> Retry
                    </button>
                )}
            </div>
        );
    }

    if (isLoading || !hasData) {
        return (
            <div className="flex items-center justify-center py-4">
                <div
                    className="animate-spin rounded-full h-8 w-8 border-b-2"
                    style={{ borderColor: mutedColor }}
                />
            </div>
        );
    }

    return <div className="flex flex-col items-center justify-center py-4">{children}</div>;
}
